import { Field } from "@components/controls/Field"
import { RichSelect } from "@components/controls/RichSelect"
import { PaymentMethodValue, StripePaymentField } from "@components/stripe/StripePaymentField"
import { TextInput } from "@components/controls/TextInput"
import { useAddressConfig } from "@features/accounts/hooks/useAddressConfig"
import { useBranches } from "@hooks/queries"
import { createUniqueFieldSchema, useEnumValues, useFieldInfo, useTsController } from "@ts-react/form"
import { ComponentPropsWithoutRef, PropsWithChildren, useId, useMemo } from "react"
import { z } from "zod"
import { FormLayout as F, FormLayout, useDescription } from "./ZodForm"
import { ErrorMessage } from "@components/atoms/ErrorMessage"
import { Checkbox } from "@components/controls/Checkbox"
import { twJoin } from "tailwind-merge"
import { SimpleSelect } from "@components/controls/SimpleSelect"

export const fieldTypes = {
  fullName: createUniqueFieldSchema(
    z
      .object({
        first: z.string().min(1),
        last: z.string().min(1),
      })
      .refine((v) => v.first && v.last, "This field is required"),
    "fullName"
  ),
  password: createUniqueFieldSchema(z.string(), "password"),
  memberNumberOrEmail: createUniqueFieldSchema(
    z
      .string()
      .describe("Member No. or Email Address")
      .transform((v) => v.trim())
      .refine((v) => v.length > 0, {
        message: "Please enter either your member number or email address.",
      }),
    "memberNumberOrEmail"
  ),
  email: createUniqueFieldSchema(z.string().email(), "email"),
  currentPassword: createUniqueFieldSchema(z.string(), "currentPassword"),
  confirmEmail: createUniqueFieldSchema(
    z
      .object({
        value: z.string().email(),
        confirm: z.string().email(),
      })
      .superRefine((v, ctx) => {
        if (v.value !== v.confirm) {
          ctx.addIssue({
            message: "Emails do not match",
            code: "custom",
          })
        }
      }),
    "confirmEmail"
  ),
  confirmPassword: createUniqueFieldSchema(
    z
      .object({
        value: z.string(),
        confirm: z.string(),
      })
      .superRefine((v, ctx) => {
        if (v.value.length < 8 || !v.value.match(/[0-9]/) || !v.value.match(/[a-z]/) || !v.value.match(/[A-Z]/)) {
          ctx.addIssue({
            message: "Password must be at least 8 characters and contain a number, lowercase letter and uppercase letter",
            code: "custom",
          })
        } else if (v.value !== v.confirm) {
          ctx.addIssue({
            message: "Passwords do not match",
            code: "custom",
          })
        }
      }),
    "confirmPassword"
  ),
  newPassword: createUniqueFieldSchema(
    z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[0-9]/, "Must contain a number, lowercase letter and uppercase letter")
      .regex(/[a-z]/, "Must contain a number, lowercase letter and uppercase letter")
      .regex(/[A-Z]/, "Must contain a number, lowercase letter and uppercase letter"),
    "newPassword"
  ),
  address: createUniqueFieldSchema(
    z.object({
      address1: z.string().min(5, "Please enter a valid address"),
      address2: z.string(),
      city: z.string().min(2, "Please enter a valid city"),
      postcode: z.string(),
      state: z.string(),
      country: z.string().min(2, "Please select a country"),
    }),
    "address"
  ),
  pronouns: createUniqueFieldSchema(z.enum(["She/her", "He/him", "They/them", "Prefer not to say"]), "pronouns"),
  addressRequired: createUniqueFieldSchema(
    z.object({
      address1: z.string().min(5, "Please enter a valid address"),
      address2: z.string(),
      city: z.string().min(2, "Please enter a valid city"),
      postcode: z.string().min(4, "Please enter a valid postcode"),
      state: z.string().min(2, "Please select a valid state"),
      country: z.string().min(2, "Please enter a valid country"),
    }),
    "addressRequired"
  ),
  date: createUniqueFieldSchema(z.string(), "date"),
  branch: createUniqueFieldSchema(z.string(), "branch"),
  select: createUniqueFieldSchema(z.string(), "select"),
  phone: createUniqueFieldSchema(z.string().min(4, "Please enter a valid phone number"), "phone"),
  paymentMethod: createUniqueFieldSchema(
    z.object({
      stripeIntent: z.string().optional(),
      savedCardId: z.string().optional(),
    }),
    "cardDetails"
  ),
  custom: createUniqueFieldSchema,
  createEnumMapping(enumType: z.ZodType) {
    return [enumType, SelectField] as [z.ZodType<any>, React.ComponentType]
  },
}

export const defaultMapping = [
  [fieldTypes.email, Email],
  [fieldTypes.confirmEmail, ConfirmedEmailField],
  [fieldTypes.memberNumberOrEmail, TextField],
  [fieldTypes.confirmPassword, ConfirmedPasswordField],
  [fieldTypes.password, PasswordField],
  [fieldTypes.newPassword, PasswordField],
  [fieldTypes.currentPassword, PasswordField],
  [fieldTypes.fullName, FullNameField],
  [fieldTypes.address, AddressField],
  [fieldTypes.addressRequired, AddressField],
  [fieldTypes.branch, BranchField],
  [fieldTypes.select, SelectField],
  [fieldTypes.pronouns, SelectField],
  [fieldTypes.date, DateField],
  [fieldTypes.phone, PhoneField],
  [fieldTypes.paymentMethod, PaymentField],
  // [fieldTypes.role, RoleField],
  // [z.array(fieldTypes.role), MultiRoleField],
  [z.string(), TextField],
  [z.boolean(), CheckBoxField],
  [z.number(), NumberField],
] as const

function AutoFieldWrapper(props: PropsWithChildren<{ showLabel?: boolean }>) {
  const { field, error } = useTsController<string>()
  const { label, note } = useDescription()
  const info = useFieldInfo()
  const required = !info.isOptional && !info.isNullable

  return (
    <Field label={label ?? field.name} note={note} required={required} error={error?.errorMessage}>
      {props.children}
    </Field>
  )
}

function BaseTextField(props: ComponentPropsWithoutRef<"input">) {
  const { field } = useTsController<string>()
  const { placeholder } = useDescription()

  return (
    <AutoFieldWrapper>
      <TextInput value={field.value ?? ""} onChange={(e) => field.onChange(e.currentTarget.value)} {...props} placeholder={props.placeholder ?? placeholder} />
    </AutoFieldWrapper>
  )
}

function TextField() {
  return <BaseTextField type="text" />
}

function DateField() {
  return <BaseTextField type="date" />
}

function PhoneField() {
  return <BaseTextField type="text" inputMode="tel" />
}

function PasswordField() {
  return <BaseTextField type="password" />
}

function Email() {
  return <BaseTextField type="email" placeholder="you@internet.com" />
}

function ConfirmedEmailField() {
  const { field, error } = useTsController<{ value: string; confirm: string }>()

  return (
    <Field error={error?.errorMessage || error?.value?.errorMessage}>
      <F.Grid>
        <Field label="Email" required>
          <TextInput
            type="email"
            name="email"
            autoComplete="email"
            value={field.value?.value ?? ""}
            onChange={(e) =>
              field.onChange({
                ...field.value,
                value: e.currentTarget.value,
              })
            }
          />
        </Field>
        <Field label="Confirm Email" required>
          <TextInput
            type="email"
            name="confirm_email"
            autoComplete="off"
            value={field.value?.confirm ?? ""}
            onChange={(e) =>
              field.onChange({
                ...field.value,
                confirm: e.currentTarget.value,
              })
            }
          />
        </Field>
      </F.Grid>
    </Field>
  )
}

function ConfirmedPasswordField() {
  const { field, error } = useTsController<{ value: string; confirm: string }>()

  return (
    <Field error={error?.errorMessage} note="Your password must be 8 characters long, containing a mix of uppercase & lowercase letters, and at least one number.">
      <F.Grid>
        <Field label="Password" required error={error?.value?.errorMessage}>
          <TextInput
            type="password"
            name="password"
            autoComplete="new-password"
            value={field.value?.value ?? ""}
            onChange={(e) =>
              field.onChange({
                ...field.value,
                value: e.currentTarget.value,
              })
            }
          />
        </Field>
        <Field label="Confirm Password" required error={error?.confirm?.errorMessage}>
          <TextInput
            type="password"
            name="confirm_password"
            autoComplete="new-password"
            value={field.value?.confirm ?? ""}
            onChange={(e) =>
              field.onChange({
                ...field.value,
                confirm: e.currentTarget.value,
              })
            }
          />
        </Field>
      </F.Grid>
    </Field>
  )
}

function CheckBoxField() {
  const id = useId()
  const { field, error } = useTsController<boolean>()
  const { note } = useDescription()

  return (
    <div className="@container">
      <div className="flex items-start">
        <div className="w-8 lg:w-7 flex-none">
          <Checkbox id={id} checked={field.value} onChange={(e) => field.onChange(!!e)} />
        </div>
        <div className="self-center">
          <label htmlFor={id} className="type-body-s-roman block">
            {note}
          </label>
        </div>
      </div>
      {!!error && <div className="pl-8 lg:pl-7 type-body-s-roman text-error-dark mt-2">{error.errorMessage}</div>}
    </div>
  )
}

function NumberField() {
  const { field, error } = useTsController<number>()

  return <TextInput type="number" value={field.value ?? ""} onChange={(e) => field.onChange(e.currentTarget.valueAsNumber)} />
}

function FullNameField() {
  const { field, error } = useTsController<z.infer<typeof fieldTypes.fullName>>()
  const { note } = useDescription()

  return (
    <Field note={note} error={error?.errorMessage}>
      <FormLayout.Grid>
        <Field label="First Name" required error={error?.first?.errorMessage}>
          <TextInput
            name="firstName"
            type="text"
            autoComplete="given-name"
            disabled={field.disabled}
            value={field.value?.first ?? ""}
            onChange={(e) => field.onChange({ ...field.value, first: e.currentTarget.value })}
          />
        </Field>
        <Field label="Last Name" required error={error?.last?.errorMessage}>
          <TextInput
            name="lastName"
            type="text"
            autoComplete="family-name"
            disabled={field.disabled}
            value={field.value?.last ?? ""}
            onChange={(e) => field.onChange({ ...field.value, last: e.currentTarget.value })}
          />
        </Field>
      </FormLayout.Grid>
    </Field>
  )
}

function SelectField() {
  const { field } = useTsController<string>()
  const vals = useEnumValues()
  const { placeholder } = useDescription()

  const options = useMemo(() => [...vals.map((v) => ({ label: v, value: v }))], [])

  return (
    <AutoFieldWrapper>
      <RichSelect placeholder={placeholder} value={options.find((o) => o.value === field.value)?.value} onChange={(v) => field.onChange(v)} options={options} />
    </AutoFieldWrapper>
  )
}

function BranchField() {
  const { field } = useTsController<string>()
  const { placeholder } = useDescription()
  const branches = useBranches(undefined)

  const options =
    branches.data?.branches?.nodes
      .filter((node) => node.branchSettings?.branchFlags?.includes("canJoin"))
      .map((branch) => ({ value: String(branch.databaseId!), label: branch.name! })) ?? []

  return (
    <AutoFieldWrapper>
      <SimpleSelect
        placeholder={placeholder ?? ""}
        value={typeof field.value === "string" ? field.value : ""}
        onChange={(v) => field.onChange(v.currentTarget.value)}
        options={options}
      />
    </AutoFieldWrapper>
  )
}

function PaymentField() {
  const { field, error } = useTsController<PaymentMethodValue>()

  return (
    <>
      <div>{JSON.stringify(error)}</div>
      {error?.errorMessage && <ErrorMessage>{error.errorMessage}</ErrorMessage>}
      <StripePaymentField value={field.value} onChange={field.onChange} />
    </>
  )
}

function AddressField() {
  const { field, error } = useTsController<z.infer<typeof fieldTypes.address>>()
  const { note } = useDescription()
  const addressConfig = useAddressConfig()

  const country = useMemo(() => {
    return addressConfig.countries.find((c) => c.value === field.value?.country)
  }, [addressConfig.countries, field.value?.country])

  const countryOptions = useMemo(() => {
    return addressConfig.countries.map((country) => {
      return {
        label: country.label,
        value: country.value,
      }
    })
  }, [addressConfig.countries])

  const stateOptions = useMemo(() => {
    return (
      country?.states.map((state) => {
        return {
          label: state.label,
          value: state.value,
        }
      }) ?? []
    )
  }, [country])

  return (
    <F.Stack>
      <Field label="Address (Line 1)" required error={error?.address1?.errorMessage} grow>
        <TextInput
          type="text"
          autoComplete="address-line1"
          placeholder="Line 1"
          disabled={field.disabled}
          value={field.value?.address1 ?? ""}
          onChange={(e) => field.onChange({ ...field.value, address1: e.currentTarget.value })}
        />
      </Field>
      <Field label="Address (Line 2)" error={error?.address2?.errorMessage} grow>
        <TextInput
          type="text"
          autoComplete="address-line2"
          placeholder="Line 2"
          disabled={field.disabled}
          value={field.value?.address2 ?? ""}
          onChange={(e) => field.onChange({ ...field.value, address2: e.currentTarget.value })}
        />
      </Field>
      <F.Grid>
        <Field label={country?.locale.city?.label ?? "City"} required error={error?.city?.errorMessage}>
          <TextInput
            type="text"
            autoComplete="address-city"
            placeholder=""
            disabled={field.disabled}
            value={field.value?.city ?? ""}
            onChange={(e) => field.onChange({ ...field.value, city: e.currentTarget.value })}
          />
        </Field>
        <Field label={country?.locale.postcode?.label ?? "Postcode"} required error={error?.postcode?.errorMessage}>
          <TextInput
            type="text"
            autoComplete="postal-code"
            inputMode="numeric"
            placeholder=""
            disabled={field.disabled}
            value={field.value?.postcode ?? ""}
            onChange={(e) => field.onChange({ ...field.value, postcode: e.currentTarget.value })}
          />
        </Field>
        <Field label="Country" required>
          <SimpleSelect
            placeholder=""
            value={addressConfig.countries.find((c) => c.value === field.value?.country)?.value}
            onChange={(e) => {
              field.onChange({ ...field.value, country: e.currentTarget.value, state: "" })
            }}
            options={countryOptions}
          />
        </Field>
        {!!country?.locale.state?.hidden || !stateOptions.length ? null : (
          <Field label={country?.locale.state?.label ?? "State"} required>
            <SimpleSelect
              placeholder=""
              value={stateOptions.find((c) => c.value === field.value?.state)?.value}
              onChange={(e) => {
                field.onChange({ ...field.value, state: e.currentTarget.value })
              }}
              options={stateOptions}
            />
          </Field>
        )}
      </F.Grid>
    </F.Stack>
  )
}
